import { useId, useState } from "react";
import type { FormEvent, MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { Why } from "@/components/Why";
import { demoApi, problemMessage } from "@/api/client";
import { formatMoney } from "@/lib/format";
import type { CashFlowReport } from "@/api/types";
import "./ReportsCashFlow.css";

type ReportStatus = "idle" | "loading" | "ready" | "error";

/** 30 days back from today, UTC — same rolling-window default
 *  `ReportsProductionSales.tsx`'s own `defaultRangeStart` uses, duplicated on purpose
 *  (that file's own reasoning: each report's own default is free to diverge later). */
function defaultRangeStart(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 30);
  return d.toISOString().slice(0, 10);
}

function defaultRangeEnd(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Cash flow, actual — Reports' historical inflow/outflow/net by calendar month, from the
 * farm's already-posted activity, for a window the owner chooses. ef-052-1, ADR 0009 §6
 * ("Reports is one picker" — a report renders inside the template at `/reports/{slug}`,
 * no area tab strip of its own; "All reports" below is the only way back).
 *
 * <p>
 * NOT the Home dashboard's forward-looking cash-flow planner
 * (`components/CashFlowPlannerCard.tsx`) — that card PROJECTS from payroll schedules and
 * upcoming bills; this screen reads what already happened, grouped by month.
 * </p>
 *
 * <p>
 * NOTHING IS FETCHED UNTIL Apply IS PRESSED — same "no guessed window" discipline
 * `ReportsProductionSales.tsx`'s own remarks give, restated here for the identical
 * reason: a cash figure read against a window nobody asked for is a number a farmer did
 * not request. The From/To inputs still carry a sensible rolling-30-day default so the
 * form is never two empty fields.
 * </p>
 *
 * <p>
 * NO ENTITY PICKER — unlike `ReportsScheduleF.tsx`/`ReportsPayrollRegister.tsx`, this
 * screen names no `entityId` at all. `CashFlowReportEndpoints` resolves the tenant's own
 * single/oldest entity when it is omitted, which is every farm this booth demo models —
 * same call `ReportsProductionSales.tsx` already makes.
 * </p>
 *
 * <p>
 * Money face applies to Inflow/Outflow/Net only — Month is a plain label, never
 * currency-formatted (`format.ts`'s own numeric-face discipline).
 * </p>
 */
export function ReportsCashFlow() {
  const fromId = useId();
  const toId = useId();

  const [from, setFrom] = useState(defaultRangeStart);
  const [to, setTo] = useState(defaultRangeEnd);

  const [report, setReport] = useState<CashFlowReport | null>(null);
  const [status, setStatus] = useState<ReportStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const rangeIsBackwards = from !== "" && to !== "" && to < from;
  const canSubmit = from !== "" && to !== "" && !rangeIsBackwards && status !== "loading";

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;

    setStatus("loading");
    setError(null);
    try {
      const result = await demoApi.getCashFlowReport({ from, to });
      setReport(result);
      setStatus("ready");
    } catch (err) {
      setReport(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the cash flow report."));
    }
  }

  /**
   * The export is its own request, fired on the click — not fetched alongside the
   * report, same reasoning `ReportsProductionSales.tsx`'s own identical download gives:
   * paying for the whole CSV body on every date change would be wasted work for the
   * visits that never press the button.
   */
  const [csvState, setCsvState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadCsv(): Promise<void> {
    if (!canSubmit) return;
    setCsvState({ kind: "busy" });
    try {
      const csv = await demoApi.getCashFlowCsv({ from, to });

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline `ReportsProductionSales.tsx`'s own
      // download follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `easyfarm-cash-flow-${from}-to-${to}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setCsvState({ kind: "done", rows });
    } catch (err) {
      setCsvState({
        kind: "error",
        message: problemMessage(err, "Could not build the cash flow file."),
      });
    }
  }

  function handleAllReportsClick(event: MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    // Self-contained, same device `ReportsProductionSales.tsx`'s own handler already
    // uses rather than threading a `navigate` prop through every screen this job
    // touches: pushState doesn't fire popstate on its own, so App.tsx's existing
    // listener is told directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  const months = report?.months ?? [];
  const isEmpty = status === "ready" && months.length === 0;

  return (
    <main className="cash-flow">
      <PageHeader
        title="Cash flow, actual"
        subtitle="Inflow, outflow and net, by month, from your already-posted activity"
      />

      <a href="/reports" className="cash-flow__all-reports" onClick={handleAllReportsClick}>
        ← All reports
      </a>

      <section
        className="cash-flow__panel"
        aria-labelledby="cash-flow-heading"
        data-state={isEmpty ? "empty" : status}
      >
        <h2 id="cash-flow-heading" className="cash-flow__heading">
          By month
        </h2>

        <form className="cash-flow__form" onSubmit={(event) => void handleSubmit(event)}>
          <div className="cash-flow__field">
            <label htmlFor={fromId}>From</label>
            <input
              id={fromId}
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </div>
          <div className="cash-flow__field">
            <label htmlFor={toId}>To</label>
            <input
              id={toId}
              type="date"
              value={to}
              aria-invalid={rangeIsBackwards || undefined}
              onChange={(event) => setTo(event.target.value)}
            />
          </div>
          <button type="submit" className="btn btn--primary cash-flow__apply" disabled={!canSubmit}>
            {status === "loading" ? "Loading…" : "Apply"}
          </button>
        </form>

        {rangeIsBackwards && (
          <p className="cash-flow__field-error" role="alert">
            The end of the period cannot be before its start.
          </p>
        )}

        <Why>
          <strong>Inflow</strong>/<strong>outflow</strong> come from already-posted income
          and expense entries only — the forward-looking projection lives on the Home
          dashboard.
        </Why>

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the cash flow report."
            detail={error ?? undefined}
          />
        )}

        {isEmpty && (
          <StateMessage
            tone="empty"
            title="Nothing to show for this window."
            detail="No income or expense activity was found."
          />
        )}

        {status === "ready" && months.length > 0 && (
          <>
            <div
              className="cash-flow__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="cash-flow-heading"
            >
              <table className="cash-flow__table ruled-rows">
                <caption className="cash-flow__caption">
                  {months.length} {months.length === 1 ? "month" : "months"}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Month</th>
                    <th scope="col" className="cash-flow__num">Inflow</th>
                    <th scope="col" className="cash-flow__num">Outflow</th>
                    <th scope="col" className="cash-flow__num">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((row) => (
                    <tr key={row.month}>
                      <th scope="row" className="cash-flow__month">{row.month}</th>
                      <td className="cash-flow__num cash-flow__amount">{formatMoney(row.inflow)}</td>
                      <td className="cash-flow__num cash-flow__amount">{formatMoney(row.outflow)}</td>
                      <td className="cash-flow__num cash-flow__amount">{formatMoney(row.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="cash-flow__export">
              <h3 className="cash-flow__export-title">Cash flow for your records</h3>
              <p className="cash-flow__export-copy">
                The same rows above, as a CSV a spreadsheet can open.
              </p>
              <button
                type="button"
                className="btn btn--secondary cash-flow__export-btn"
                disabled={csvState.kind === "busy" || !canSubmit}
                onClick={() => void downloadCsv()}
              >
                {csvState.kind === "busy" ? "Building the file…" : "Download CSV"}
              </button>

              {csvState.kind === "done" && (
                <p className="cash-flow__export-result" role="status">
                  {csvState.rows} {csvState.rows === 1 ? "line" : "lines"} saved.
                </p>
              )}

              {csvState.kind === "error" && (
                <p className="cash-flow__export-error" role="alert">
                  {csvState.message} Nothing was downloaded.
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
